var _AttachFile, _Send;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState } from 'react';
import { inputClasses } from '@mui/material/Input';
import { func, object, string, shape } from 'prop-types';
import clsx from 'clsx';
import { AttachFile, Send } from 'mosaic-react-icons/';
import { styled, useTheme } from '@mui/material/styles';
import SovosIconButton from '../sovos-icon-button';
import SovosTextField from '../sovos-text-field';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const StyledCommentForm = styled('div')(_ref => {
  let {
    theme: {
      palette,
      shape: themeShape,
      spacing
    }
  } = _ref;
  return {
    alignItems: 'flex-start',
    backgroundColor: palette.background.default,
    border: "1px solid ".concat(palette.divider),
    borderRadius: themeShape.borderRadius,
    display: 'flex',
    paddingBottom: spacing(3),
    paddingInlineEnd: spacing(),
    paddingInlineStart: spacing(2),
    paddingTop: spacing(3)
  };
});
const StyledTextField = styled(SovosTextField)(_ref2 => {
  let {
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref2;
  return {
    // Use Mui-textfield root class
    '&.sovosCommentForm__commentField': {
      minHeight: spacing(5),
      paddingTop: spacing(0.75),
      width: '100%',
      ["& .".concat(inputClasses.root)]: {
        color: palette.text.secondary,
        ...typography.body1,
        ["&.".concat(inputClasses.error)]: {
          whiteSpace: 'pre'
        }
      }
    }
  };
});
const SovosCommentForm = _ref3 => {
  let {
    attachmentButtonTooltipText,
    className,
    commentPlaceholderLabel,
    errorLabels,
    onAttachmentClicked,
    onSubmit,
    onCommentContentChange,
    style,
    submitButtonTooltipText,
    ...rest
  } = _ref3;
  const {
    t
  } = useMosaicTranslation();
  const {
    palette
  } = useTheme();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const textFieldPlaceholderLabel = commentPlaceholderLabel || t('commentForm.commentPlaceholder');
  const validate = newComment => {
    if (newComment && newComment.length > 500) {
      setError((errorLabels === null || errorLabels === void 0 ? void 0 : errorLabels.length) || t('commentForm.errorLength'));
      return false;
    }
    setError('');
    return true;
  };
  const handleCommentChanged = event => {
    const newComment = event.target.value;
    if (onCommentContentChange) {
      onCommentContentChange(newComment);
    }
    setComment(newComment);
    validate(newComment);
  };
  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!comment || comment.length === 0) {
      setError((errorLabels === null || errorLabels === void 0 ? void 0 : errorLabels.empty) || t('commentForm.errorEmpty'));
      return;
    }
    onSubmit(comment);
    setComment('');
    setError('');
  };
  return /*#__PURE__*/React.createElement(StyledCommentForm, _extends({
    className: clsx('sovosCommentForm', className),
    style: style
  }, rest), /*#__PURE__*/React.createElement(StyledTextField, {
    InputProps: {
      disableUnderline: true
    },
    "aria-label": textFieldPlaceholderLabel,
    className: "sovosCommentForm__commentField",
    error: !!error,
    helperText: error,
    multiline: true,
    name: "add-comment",
    placeholder: textFieldPlaceholderLabel,
    value: comment,
    variant: "standard",
    onChange: handleCommentChanged
  }), onAttachmentClicked ? /*#__PURE__*/React.createElement(SovosIconButton, {
    tooltipText: attachmentButtonTooltipText || t('commentForm.attachmentButtonTooltipText'),
    className: "sovosCommentForm__addAttachmentButton",
    onClick: onAttachmentClicked
  }, _AttachFile || (_AttachFile = /*#__PURE__*/React.createElement(AttachFile, null))) : null, /*#__PURE__*/React.createElement(SovosIconButton, {
    className: "sovosCommentForm__submitButton",
    color: palette.text.primary,
    tooltipText: submitButtonTooltipText || t('commentForm.submitButton'),
    onClick: handleSubmit
  }, _Send || (_Send = /*#__PURE__*/React.createElement(Send, null))));
};
SovosCommentForm.propTypes = {
  /**
   * Tooltip for attachment button
   */
  attachmentButtonTooltipText: string,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Placeholder text for the comment form
   */
  commentPlaceholderLabel: string,
  /**
   * Text for the error message
   */
  errorLabels: shape({
    empty: string.isRequired,
    length: string.isRequired
  }),
  /**
   * Callback fired when the submit button is clicked
   */
  onSubmit: func.isRequired,
  /**
   * Callback fired when the attachment icon is clicked
   */
  onAttachmentClicked: func,
  /**
   * Callback fired when the input value is changed `function(comment:
   * string) => void`
   */
  onCommentContentChange: func,
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Tooltip for the submit button
   */
  submitButtonTooltipText: string
};
export default SovosCommentForm;